import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AppService } from '../../../services/app.service';

import { BcContestService } from '../../../services/bc-contest.service';
import { helpers, ICourseCard, bcState, getMessageTemplates } from '../shared';
import { IBcActiveContest, IBcContestStudentData } from '../../../models/BcContest';
import { MessageService } from '../../../services/message.service';

@Component({
    selector: 'bc-contest-course-select',
    templateUrl: './bc-contest-course-select.component.html',
    styleUrls: ['../bc-contest.scss']
})
export class BcContestCourseSelectComponent implements OnInit {
    constructor(
        public app: AppService,
        private service: BcContestService,
        private messages: MessageService,
        private route: ActivatedRoute
    ) { }

    contest: IBcActiveContest;
    student: IBcContestStudentData;
    courses: ICourseCard[] = [];

    limit: number = 0;
    limitWarning: string;
    pointsExtraInfoHtml: string;
    upn: string;

    isFilterExtended: boolean;
    isReady: boolean;

    readonly filters: {
        text?: string,
        language: { value?: string, options: string[] },
        address?: string,
        manager?: string,
        examinationType: { value?: string, options: string[] },
        onlyAdded: boolean
    } = {
            language: { options: [] },
            examinationType: { options: [] },
            onlyAdded: false
        };

    get isLimitReached() {
        return this.courses.filter(t => !!t.ApplicationId).length >= this.limit;
    }

    get requiredEcts() {
        return (this.student?.CreditPoints || 0) * 1.5;
    }

    private studentId: number;

    get filteredCourses(): ICourseCard[] {
        const text = (this.filters.text || '').toLowerCase();
        const lang = (this.filters.language.value || '').toLowerCase();
        const address = (this.filters.address || '').toLowerCase();;
        const manager = (this.filters.manager || '').toLowerCase();
        const exam = (this.filters.examinationType.value || '').toLowerCase();

        const list = this.courses.filter(t => {
            if (this.filters.onlyAdded && !t.ApplicationId) return false;
            if (lang && !t.Language?.toLowerCase().includes(lang)) return false;
            if (manager && !t.Manager.toLowerCase().includes(manager)) return false;
            if (exam && !t.ExaminationType?.toLowerCase().includes(exam)) return false;
            if (address && t.groups.filter(n => n.Location?.toLowerCase().includes(address)).length == 0) return false;

            if (text) {
                if (!t.Language?.toLowerCase().includes(text)
                    && !t.Manager?.toLowerCase().includes(text)
                    && !t.ExaminationType?.toLowerCase().includes(text)
                    && !t.Subject?.toLowerCase().includes(text)
                    && !t.SubjectCode?.toLowerCase().includes(text)
                    && !t.Department?.toLowerCase().includes(text)
                    && !t.CreditPoints.toString().includes(text)
                    && !t.Ects.toString().includes(text)
                    && t.groups.filter(n => n.Location?.toLowerCase().includes(text)
                        || n.GroupDay?.toLowerCase().includes(text)
                        || n.ExaminationType?.toLowerCase().includes(text)
                    ).length == 0
                ) {
                    return false;
                }
            }

            return true;
        });

        return list;
    }

    ngOnInit() {
        this.studentId = this.route.snapshot.queryParams['studentId'];
        this.upn = this.route.snapshot.queryParams['upn'];

        if (!this.studentId) {
            this.back();
            return;
        }

        getMessageTemplates(this.app, this.messages).subscribe(data => {
            this.pointsExtraInfoHtml = data.pointInfo;
        });

        this.contest = bcState.getActiveContest();
        this.student = bcState.getStudentData();

        if (this.contest && this.student?.HasApplied) {
            this.load();
        } else {
            this.app.addLoading(this.service.getActive()).subscribe(data => {
                if (!data) {
                    this.back();
                } else {
                    bcState.setActiveContest(data);

                    this.app.addLoading(this.service.getStudentData(data.Id, this.upn)).subscribe(data => {
                        this.student = data.find(t => t.Id == this.studentId);

                        if (this.student?.HasApplied) {
                            bcState.setStudentData(this.student);
                            this.load();
                        } else {
                            this.back();
                        }
                    });
                }
            });
        }
    }

    clearFilter() {
        this.filters.address = undefined;
        this.filters.examinationType.value = undefined;
        this.filters.language.value = undefined;
        this.filters.manager = undefined;
        this.filters.onlyAdded = false;
        this.filters.text = undefined;
    }

    onAdd(course: ICourseCard) {
        bcState.requestOpenPreferences();
    }

    onRemove(course: ICourseCard) {
        bcState.requestOpenPreferences();
    }

    // back and finish are not equal by logic, though may have the same implementation...

    back() {
        this.app.navigate(['/bc-contests/apply'], { queryParams: { upn: this.upn } });
    }

    finish() {
        this.app.navigate(['/bc-contests/apply'], { queryParams: { upn: this.upn } });
    }

    private load() {
        this.app.addLoading(this.service.getStudentCourses(this.studentId, true)).subscribe(data => {
            const gc = helpers.groupCourses(data);

            this.courses = gc.courses;
            this.filters.examinationType.options = gc.examinationTypes;
            this.filters.language.options = gc.languages;

            const contest = bcState.getActiveContest();

            this.limit = contest.CourseLimit || 0;
            this.limitWarning = this.app.translate('bcContestApply_courseLimitWarning').replace('{{limit}}', this.limit);

            this.isReady = true;
        });
    }
}
